import { UtilService } from 'src/app/core/services/util/util.service';
import { TextAdDimensions } from './google-text-ad.module';
import { Injectable, NgZone, OnDestroy, effect, inject } from '@angular/core';
import { Observable, ReplaySubject, Subject, Subscription } from 'rxjs';
import { Breakpoints, BreakPointSizeService } from 'src/app/core/services/break-point-size/break-point-size.service';
import { StorageService } from 'src/app/core/services/storage/storage.service';
import { BrowserPlatformService } from 'src/app/core/services/browser-platform/browser-platform.service';

/**
 * Ads unit codes - Aka: https://admanager.google.com/108500498#inventory/ad_unit/list
 */
export enum AdsType {
    MediumRectangle = 'mobile-300-250', //not only mobile
    RectangleAd = 'mobile-article-ad-350-100-heb', // For some reason ads always empty, don't use this.
    DesktopRecAd = 'Desktop-article-ad-498-92-heb2',
    BigRectangle = 'desktop-336-280',
    BigSquare = 'mobile-maavron-360-360',
    Popunder = 'desktop-1000-150',
    // PopunderSlim = 'desktop-1000-90',
    VerticalBanner = 'desktop-120-240',
    WideVerticalBanner = 'desktop-side-tower',
    BigWideVerticalBanner = 'wide-tower-300-600',
    Leaderboard = 'desktop-728-90',
    Leaderboardmobile = '320-50',
    SuperLeaderboard = '970-90',
    Billboard = '970-250',
    MobileRectangle = 'Mobile_daytimes', // 320x100
}

@Injectable({
    providedIn: 'root',
})
export class GoogleTextAdService implements OnDestroy {
    private _breakPointSizeService = inject(BreakPointSizeService);
    private _storage = inject(StorageService);
    private _utils = inject(UtilService);
    private zone = inject(NgZone);
    private _browser = inject(BrowserPlatformService);

    readonly COUNT_AD_TOP_KEY: string = 'count-ad-top';
    public countAdTop = 0;
    private _lastAdNumber = 0;
    readonly CYCLE_AD_NUMBER = 3;
    private _adDimensions: { [key: string]: TextAdDimensions } = {};
    public adsType: typeof AdsType = AdsType;
    private _currBreakPoint: Breakpoints | null = null;
    private _subscriptions: Subscription[] = [];

    constructor() {
        const _breakPointSizeService = this._breakPointSizeService;

        this._buildArray();
        this._isEnableAdStickyFooterSub = new Subject<boolean>();
        effect(() => {
            const currBreakPoint = _breakPointSizeService.currBreakpoint();
            this._currBreakPoint = currBreakPoint;
            let adTop: AdsType;
            // ***************
            // **For break 1**
            if ([Breakpoints.Normal, Breakpoints.Wide].includes(currBreakPoint)) {
                adTop = this.adsType.Billboard;

                // adTop = this._getAdTopForDesktop();

                // ***************
                // **For break 2**
            } else if ([Breakpoints.Narrow, Breakpoints.Middle].includes(currBreakPoint)) {
                adTop = this.adsType.Billboard;
                // ***************
                // **For break 3**
            } else {
                adTop = this.adsType.BigSquare;
            }
            if (this.currAdTopType !== adTop) {
                this.setCurrAdTopType(adTop);
            }
        });
        this._subscriptions.push(
            this.getIsAdTopEnable().subscribe((isAdTopEnable) => {
                if (isAdTopEnable) {
                    this.countAdTop = this._storage.load(this.COUNT_AD_TOP_KEY);
                    if (this._currBreakPoint && [Breakpoints.Normal, Breakpoints.Wide].includes(this._currBreakPoint)) {
                        let adTop = this._getAdTopForDesktop();
                        if (this.currAdTopType !== adTop) {
                            this.setCurrAdTopType(adTop);
                        }
                    }

                    this._storage.store(this.COUNT_AD_TOP_KEY, (this.countAdTop + 1) % this.CYCLE_AD_NUMBER ? this.countAdTop + 1 : 0);
                }
            }),
        );
        // (window as any).addEventListener('load', (event: any) => {
        // Commenting out since logic changed to manual loading of ads after display method.
        //     this.refreshAllAds();
        // });
    }

    private _getAdTopForDesktop(): AdsType {
        return this.countAdTop % this.CYCLE_AD_NUMBER ? this.adsType.SuperLeaderboard : this.adsType.Billboard;
    }

    public getAdTag(): string {
        return `yeshivaAd${++this._lastAdNumber}`;
    }

    public getAdDimensions(adCode: string): TextAdDimensions {
        return this._adDimensions[adCode];
    }

    private _buildArray(): void {
        this._adDimension(AdsType.Popunder, 1000, 150);
        // this._adDimension(AdsType.PopunderSlim, 1000, 90);
        this._adDimension(AdsType.VerticalBanner, 120, 240);
        this._adDimension(AdsType.WideVerticalBanner, 160, 600);
        this._adDimension(AdsType.BigWideVerticalBanner, 300, 600);
        this._adDimension(AdsType.BigRectangle, 336, 280);
        this._adDimension(AdsType.RectangleAd, 350, 100);
        this._adDimension(AdsType.DesktopRecAd, 498, 92);
        this._adDimension(AdsType.Leaderboard, 728, 90);
        this._adDimension(AdsType.SuperLeaderboard, 970, 90);
        this._adDimension(AdsType.Leaderboardmobile, 320, 50);
        this._adDimension(AdsType.MediumRectangle, 300, 250);
        this._adDimension(AdsType.BigSquare, 360, 360);
        this._adDimension(AdsType.Billboard, 970, 250);
        this._adDimension(AdsType.MobileRectangle, 320, 100);
    }

    private _adDimension(_adCode: string, _width: number, _height: number): void {
        this._adDimensions[_adCode] = { width: _width, height: _height };
    }

    refreshAllAds() {
        this.zone.runOutsideAngular(() => {
            // Need to cast to 'any' since old modules uses googletag and has unupdated .d.ts file.
            const win = this._browser.nativeWindow as any;

            // Ad blockers prevent pubads from initiate.
            if (win?.googletag?.pubads) {
                win.googletag.pubads().refresh();
            }
        });
    }

    private _isEnableAdStickyFooterSub: Subject<boolean>;
    public getIsEnableAdStickyFooter(): Observable<boolean> {
        return this._isEnableAdStickyFooterSub.asObservable();
    }
    public setIsEnableAdStickyFooter(isEnableAdStickyFooter: boolean): void {
        this._isEnableAdStickyFooterSub.next(isEnableAdStickyFooter);
    }

    private _isAdTopEnable: Subject<boolean> = new Subject();
    public getIsAdTopEnable(): Observable<boolean> {
        return this._isAdTopEnable.asObservable();
    }
    public setIsAdTopEnable(_isAdTopEnable: boolean): void {
        this._isAdTopEnable.next(_isAdTopEnable);
    }

    public currAdTopType: AdsType | null = null;
    private _currAdTopType$ = new ReplaySubject<AdsType>(1);
    public getCurrAdTopType(): Observable<AdsType> {
        return this._currAdTopType$.asObservable();
    }
    public setCurrAdTopType(_currAdTopType: AdsType): void {
        this.currAdTopType = _currAdTopType;
        this._currAdTopType$.next(_currAdTopType);
    }

    private _isAdTopLoaded: Subject<boolean> = new Subject();
    public topAdLoaded = false;

    public getIsAdTopLoaded(): Observable<boolean> {
        return this._isAdTopLoaded.asObservable();
    }

    public setIsAdTopLoaded(_isAdTopLoaded: boolean): void {
        this._isAdTopLoaded.next(_isAdTopLoaded);
        this.topAdLoaded = _isAdTopLoaded;
    }

    public isBannerTop = false;
    public setIsBannerTop(isBannerTop: boolean) {
        this.isBannerTop = isBannerTop;
    }

    public CheckAdViewRecently(tagCode: string): boolean {
        let lastAdTime = parseInt(this._storage.getSimpleData(tagCode, false), 10);
        let twoMinues = 1000 * 60 * 2;
        return Date.now() - lastAdTime < twoMinues;
    }

    ngOnDestroy(): void {
        if (this._isAdTopEnable) {
            this._isAdTopEnable.unsubscribe();
        }
        if (this._isEnableAdStickyFooterSub) {
            this._isEnableAdStickyFooterSub.unsubscribe();
        }
        if (this._currAdTopType$) {
            this._currAdTopType$.complete();
        }
        this._utils.unsubscribe(this._subscriptions);
    }
}
